import React, { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, ShoppingBag, Heart } from "lucide-react";
import { BsnSeal } from "@/components/BsnSeal";
import { useCart } from "@/components/CartProvider";
import { CartDrawer } from "@/components/CartDrawer";
import { STORE_VISIBLE } from "@/lib/flags";
import { useLang } from "@/lib/lang";

const NavLang = () => {
  const { lang, setLang } = useLang();
  const btn = (v, label) => (
    <button onClick={() => setLang(v)} className={`px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold transition-colors ${lang === v ? "bg-bsn-gold text-bsn-midnight" : "text-bsn-ice/80 hover:text-white"}`} data-testid={`nav-lang-${v}`} aria-pressed={lang === v}>{label}</button>
  );
  return <div className="inline-flex p-0.5 rounded-full border border-bsn-gold/50" role="group" aria-label="Language">{btn("en", "EN")}{btn("es", <><span className="sm:hidden">ES</span><span className="hidden sm:inline">Español</span></>)}</div>;
};

const PRIMARY = [
  { to: "/resources", label: "Get Help", es: "Obtén ayuda", testid: "nav-link-resources" },
  { to: "/affirmations", label: "Rebuilding", es: "Reconstruir", testid: "nav-link-affirmations" },
  { to: "/stories", label: "Survivor Stories", es: "Historias de sobrevivientes", testid: "nav-link-stories" },
  { to: "/journal", label: "Journal", es: "Diario", testid: "nav-link-journal" },
  { to: "/super-kids", label: "Super Kids", es: "Super Kids", testid: "nav-link-super-kids" },
  { to: "/teen-facts", label: "Teens", es: "Jóvenes", testid: "nav-link-teens" },
  { to: "/sanctuary", label: "Sanctuary", es: "Santuario", testid: "nav-link-sanctuary" },
  ...(STORE_VISIBLE ? [{ to: "/shop", label: "Shop", es: "Tienda", testid: "nav-link-shop" }] : []),
];

const MORE = [
  { to: "/stay", label: "♪ 'Stay' — New Song", es: "♪ 'Stay' — Nueva canción", testid: "nav-link-stay" },
  { to: "/speaking", label: "Invite Mary to Speak", es: "Invita a Mary a hablar", testid: "nav-link-speaking" },
  { to: "/about", label: "About BSN", es: "Acerca de BSN", testid: "nav-link-about" },
  { to: "/mission", label: "Mission & Vision", es: "Misión y visión", testid: "nav-link-mission" },
  { to: "/training", label: "Training & Volunteering", es: "Capacitación y voluntariado", testid: "nav-link-training" },
  { to: "/contact", label: "Contact", es: "Contacto", testid: "nav-link-contact" },
];

export const Navbar = () => {
  const { lang } = useLang();
  const { count, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header data-i18n="manual" className="sticky top-0 z-[80] bg-bsn-midnight/95 backdrop-blur-md border-b border-bsn-gold/20" data-testid="navbar">
      <nav className="max-w-7xl mx-auto px-4 h-[72px] flex items-center justify-between gap-2 sm:gap-4">
        <Link to="/" data-testid="nav-link-home" className="min-w-0 shrink overflow-hidden">
          <BsnSeal size={46} withText />
        </Link>

        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          <NavLang />
          {STORE_VISIBLE && <Link to="/donations" data-testid="nav-donate-btn"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full btn-gold text-sm font-semibold transition-all">
            <Heart className="w-4 h-4" /> Donate
          </Link>}

          {STORE_VISIBLE && <button onClick={() => setOpen(true)} className="relative p-2 text-bsn-ice hover:text-bsn-gold transition-colors" data-testid="nav-cart-btn" aria-label="Open cart">
            <ShoppingBag className="w-6 h-6" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-bsn-gold text-bsn-midnight text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">
                {count}
              </span>
            )}
          </button>}

          <button onClick={() => setMobileOpen((v) => !v)} className="p-2 text-bsn-ice hover:text-bsn-gold transition-colors" data-testid="nav-mobile-toggle" aria-label="Menu" aria-expanded={mobileOpen}>
            {mobileOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="bg-bsn-navy border-t border-bsn-gold/20" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-1">
          {[...PRIMARY, ...(STORE_VISIBLE ? [{ to: "/donations", label: "Donations & Patron Gifts", es: "Donaciones", testid: "nav-link-donations" }] : []), ...MORE].map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setMobileOpen(false)} data-testid={`mobile-${l.testid}`}
              className={({ isActive }) => `block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-bsn-gold/15 text-bsn-gold" : "text-bsn-ice/85 hover:bg-white/5"}`}>
              {lang === "es" ? l.es : l.label}
            </NavLink>
          ))}
          </div>
        </div>
      )}

      <CartDrawer />
    </header>
  );
};

export default Navbar;
